import ApiService from "@/services/api.service";

class AuthenticationError extends Error {
  constructor(errorCode, message) {
    super(message);
    this.name = this.constructor.name;
    this.message = message;
    this.errorCode = errorCode;
  }
}

class ResourceNotFoundError extends Error {
  constructor(errorCode, message) {
    super(message);
    this.name = this.constructor.name;
    this.message = message;
    this.errorCode = errorCode;
  }
}

class ValidationError extends Error {
  constructor(errorCode, message, description) {
    super(message);
    this.name = this.constructor.name;
    this.message = message;
    this.errorCode = errorCode;
    this.description = description;
  }
}

class ResourceAlreadyExistingError extends Error {
  constructor(errorCode, message, description) {
    super(message);
    this.name = this.constructor.name;
    this.message = message;
    this.errorCode = errorCode;
    this.description = description;
  }
}

const OfferOldService = {
  /**
   * Returns a list of open missions
   */
  missions: async function(withoutApplications, pageCount, page, sort = null) {
    try {
      const params = new URLSearchParams();

      params.append("page_count", pageCount);
      params.append("page", page);

      if (withoutApplications) {
        params.append(
          "without_other_applications",
          withoutApplications ? 1 : 0
        );
      }

      if (sort) {
        params.append("sort", sort);
      }

      const response = await ApiService.get(
        "/missions/open?" + params.toString()
      );
      return response.data;
    } catch (error) {
      throw new AuthenticationError(error.response.status, error.response.data);
    }
  },

  /**
   * Returns a list of open missions (anonymized)
   */
  anonymizedMissions: async function(
    availability_date,
    filters,
    language,
    withoutApplications,
    pageCount,
    page,
    sort = null
  ) {
    try {
      let response;
      const params = new URLSearchParams();

      params.append("page_count", pageCount);
      params.append("page", page);

      if (sort) {
        params.append("sort", sort);
      }

      if (filters == null) filters = {};

      if (availability_date) {
        params.append("availability_date", availability_date);
      }

      if (filters.one_patient) {
        params.append(
          "one_patient",
          this.toNumericBoolean(filters.one_patient)
        );
      }

      if (filters.two_patients) {
        params.append(
          "two_patients",
          this.toNumericBoolean(filters.two_patients)
        );
      }

      if (filters.further_persons_household) {
        params.append(
          "further_persons_household",
          this.toNumericBoolean(filters.further_persons_household)
        );
      }

      if (filters.patients_fully_mobile) {
        params.append(
          "patients_fully_mobile",
          this.toNumericBoolean(filters.patients_fully_mobile)
        );
      }

      if (filters.patients_wheelchair) {
        params.append(
          "patients_wheelchair",
          this.toNumericBoolean(filters.patients_wheelchair)
        );
      }

      if (filters.patients_bedridden) {
        params.append(
          "patients_bedridden",
          this.toNumericBoolean(filters.patients_bedridden)
        );
      }

      if (filters.night_commitment_none) {
        params.append(
          "night_commitment_none",
          this.toNumericBoolean(filters.night_commitment_none)
        );
      }

      if (filters.night_commitment_once) {
        params.append(
          "night_commitment_once",
          this.toNumericBoolean(filters.night_commitment_once)
        );
      }

      if (filters.night_commitment_multiple) {
        params.append(
          "night_commitment_multiple",
          this.toNumericBoolean(filters.night_commitment_multiple)
        );
      }

      if (filters.dementia_no) {
        params.append(
          "dementia_no",
          this.toNumericBoolean(filters.dementia_no)
        );
      }

      if (filters.dementia_yes) {
        params.append(
          "dementia_yes",
          this.toNumericBoolean(filters.dementia_yes)
        );
      }

      if (filters.environment_urban) {
        params.append(
          "environment_urban",
          this.toNumericBoolean(filters.environment_urban)
        );
      }

      if (filters.environment_rural) {
        params.append(
          "environment_rural",
          this.toNumericBoolean(filters.environment_rural)
        );
      }

      if (filters.car_required_no) {
        params.append(
          "car_required_no",
          this.toNumericBoolean(filters.car_required_no)
        );
      }

      if (filters.car_required_yes) {
        params.append(
          "car_required_yes",
          this.toNumericBoolean(filters.car_required_yes)
        );
      }

      if (filters.building_type_apartment) {
        params.append(
          "building_type_apartment",
          this.toNumericBoolean(filters.building_type_apartment)
        );
      }

      if (filters.building_type_house) {
        params.append(
          "building_type_house",
          this.toNumericBoolean(filters.building_type_house)
        );
      }

      if (filters.own_room) {
        params.append("own_room", this.toNumericBoolean(filters.own_room));
      }

      if (filters.own_toilet) {
        params.append("own_toilet", this.toNumericBoolean(filters.own_toilet));
      }

      if (filters.compensation_option) {
        params.append("compensation_option", filters.compensation_option);
      }

      if (filters.gender_option) {
        params.append("gender_option", filters.gender_option);
      }

      if (filters.location_option) {
        params.append("location_option", filters.location_option);
      }

      if (withoutApplications) {
        params.append(
          "without_other_applications",
          withoutApplications ? 1 : 0
        );
      }

      params.append("set_request_language", language);

      response = await ApiService.get(
        "/missions/open-anonymised?" + params.toString(),
        {},
        {}
      );
      return response.data;
    } catch (error) {
      throw new error();
    }
  },

  toNumericBoolean(booleanValue) {
    if (booleanValue) {
      return 1;
    } else {
      return 0;
    }
  },

  /**
   * Sets the availability date for the logged in caregiver
   * @param availability_date "yyyy-MM-dd"
   */
  setAvailability: async function(availability_date) {
    try {
      await ApiService.post("/user/set-availability", {
        availability_date: availability_date
      });
    } catch (error) {
      throw new AuthenticationError(error.response.status, error.response.data);
    }
  },

  /**
   * Toggle favorite state of mission
   * @param mission_id
   */
  toggleFavorite: async function(mission_id) {
    try {
      await ApiService.post("/missions/favorites/toggle", {
        mission_id: mission_id
      });
    } catch (error) {
      throw new AuthenticationError(error.response.status, error.response.data);
    }
  },

  /**
   * Returns a detailed description of a mission
   * @param mission_id
   */
  details: async function(mission_id) {
    try {
      let response = await ApiService.get(`/missions/${mission_id}/details`);
      return response.data;
    } catch (error) {
      if (error.response.status === 404) {
        throw new ResourceNotFoundError(
          error.response.status,
          error.response.data
        );
      } else if (error.response.status === 401) {
        throw new AuthenticationError(
          error.response.status,
          error.response.data
        );
      }
    }
  },

  requestFurtherInformation: async function(mission_id, comment) {
    try {
      let response = await ApiService.post("/missions/request-further-info", {
        mission_id: mission_id,
        comment: comment
      });
      return response.data;
    } catch (error) {
      if (error.response.status === 404) {
        throw new ResourceNotFoundError(
          error.response.status,
          error.response.data
        );
      } else if (error.response.status === 401) {
        throw new AuthenticationError(
          error.response.status,
          error.response.data
        );
      } else if (error.response.status === 422) {
        throw new ValidationError(
          error.response.status,
          error.response.data,
          error.response.data.description
        );
      }
    }
  },

  /**
   * Apply for a position
   * @param mission_id
   * @param notes string
   * @param return_date yyyy-MM-dd
   */
  apply: async function(mission_id, notes, return_date) {
    try {
      await ApiService.post(`/missions/${mission_id}/apply/`, {
        notes: notes,
        return_date: return_date
      });
    } catch (error) {
      if (error.response.status === 404) {
        throw new ResourceNotFoundError(
          error.response.status,
          error.response.data
        );
      } else if (error.response.status === 401) {
        throw new AuthenticationError(
          error.response.status,
          error.response.data
        );
      } else if (error.response.status === 422) {
        throw new ValidationError(
          error.response.status,
          error.response.data,
          error.response.data.description
        );
      } else if (error.response.status === 409) {
        throw new ResourceAlreadyExistingError(
          error.response.status,
          error.response.data,
          error.response.data.description
        );
      }
    }
  },

  lastBookings: async function(page, pageSize) {
    try {
      return await ApiService.post("/last-bookings", {
        page: page,
        page_size: pageSize
      });
    } catch (error) {
      if (error.response.status === 404) {
        throw new ResourceNotFoundError(
          error.response.status,
          error.response.data
        );
      } else if (error.response.status === 401) {
        throw new AuthenticationError(
          error.response.status,
          error.response.data
        );
      } else if (error.response.status === 422) {
        throw new ValidationError(
          error.response.status,
          error.response.data,
          error.response.data.description
        );
      }
    }
  }
};

// export default OfferOldService;

export {
  OfferOldService,
  AuthenticationError,
  ResourceNotFoundError,
  ValidationError,
  ResourceAlreadyExistingError
};
